import React, { useState } from 'react';
import useAppStore from '../store';
import { currencyLabel } from '../utils/currencyFormatter';

// +++ BASIS LAYOUT +++
export const Page: React.FC<React.PropsWithChildren<{}>> = ({ children }) => (
    <div className="bg-pe-bg min-h-screen p-4 sm:p-6 lg:p-8 font-sans text-pe-text selection:bg-blue-600 selection:text-white">
        <div className="w-full max-w-7xl mx-auto space-y-8">
            {children}
        </div>
    </div>
);

export const Section: React.FC<React.PropsWithChildren<{ className?: string, title?: string }>> = ({ children, className = '', title }) => (
    <div className={`bg-pe-elevated text-pe-text p-6 rounded-lg w-full border border-pe-border shadow-sm ${className}`}>
        {title && <SectionTitle>{title}</SectionTitle>}
        {children}
    </div>
);

export const PageTitle: React.FC<React.PropsWithChildren<{ className?: string }>> = ({ children, className = '' }) => (
    <h1 className={`text-3xl font-bold text-pe-text mb-8 carbon-weave-bg rounded-xl border border-pe-border dark:border-[#2a2a2a] px-6 py-4 font-sans tracking-tight ${className}`}>
        {children}
    </h1>
);

export const SectionTitle: React.FC<React.PropsWithChildren<{}>> = ({ children }) => (
    <h2 className="text-xl font-bold text-center mb-6 text-pe-accent-text font-sans tracking-wide uppercase text-sm">
        {children}
    </h2>
);

// +++ INPUT SYSTEM (FIX: Weniger Padding rechts, damit Zahlen sichtbar bleiben) +++
interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    label?: string;
    isCurrency?: boolean;
    suffix?: string; 
    center?: boolean; 
}

export const Input: React.FC<InputProps> = ({ 
    label, 
    className = '', 
    isCurrency = false, 
    suffix,
    type,
    center = true,
    onKeyDown,
    min,
    ...rest 
}) => {
    const countryCurrency = useAppStore((s: any) => s.settings?.country?.currency);
    const selectedLand = useAppStore((s: any) => s.settings?.selectedCountryId || s.clientData?.land);
    const effectiveSuffix =
        suffix ||
        (isCurrency ? currencyLabel(countryCurrency, selectedLand) : undefined);
    const isReadOnly = rest.readOnly || rest.disabled;
    
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.currentTarget.blur(); 
        }
        // NumLock-aus: Zifferntasten senden ArrowUp/ArrowDown → Browser dekrementiert type=number
        if (type === 'number') {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
            }
            const minNum = min === undefined || min === '' ? NaN : Number(min);
            if (e.key === '-' && !Number.isNaN(minNum) && minNum >= 0) {
                e.preventDefault();
            }
        }
        if (onKeyDown) {
            onKeyDown(e);
        }
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        if (!isReadOnly) {
            e.target.select();
        }
    };

    return (
        <div className="flex flex-col gap-1 w-full">
            {label && <label className="text-xs font-semibold text-pe-muted uppercase tracking-wider ml-1 mb-1">{label}</label>}
            <div className="relative">
                <input
                    type={type}
                    min={min}
                    onKeyDown={handleKeyDown}
                    onFocus={handleFocus}
                    className={`
                        w-full rounded-md border
                        focus:outline-none transition-colors
                        [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none
                        ${center ? 'text-center' : 'text-left'}
                        
                        /* FIX: Padding rechts massiv verkleinert (pr-8 statt pr-12), damit Zahlen Platz haben */
                        ${effectiveSuffix ? 'pr-8' : 'px-3'} 
                        
                        /* Standard-Größen (können via className überschrieben werden) */
                        ${className.includes('h-') ? '' : 'py-2.5'}
                        ${className.includes('text-') ? '' : 'text-lg'}
                        ${className.includes('font-') ? '' : 'font-bold font-mono'}

                        ${isReadOnly 
                            ? 'bg-pe-tile-header text-pe-accent-text border-pe-border focus:ring-0 cursor-default' 
                            : 'bg-pe-input text-pe-text border-pe-border focus:ring-1 focus:ring-pe-accent focus:border-pe-accent' 
                        }
                        ${className}
                    `}
                    {...rest}
                />
                {effectiveSuffix && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <span className={`font-mono font-bold select-none ${className.includes('text-sm') ? 'text-sm' : 'text-lg'} ${isReadOnly ? 'text-pe-accent-text opacity-50' : 'text-gray-500'}`}>
                            {effectiveSuffix}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};

// +++ RESULT SYSTEM +++
interface ResultProps {
    label: string;
    value: string | number;
    subtext?: string;
    isHighlight?: boolean;
}

export const Result: React.FC<ResultProps> = ({ label, value, subtext, isHighlight = false }) => (
    <div className={`
        p-4 rounded-md border flex flex-col justify-center items-center h-full
        ${isHighlight ? 'bg-pe-input border-[#E6C299]/50 shadow-[0_0_10px_rgba(230,194,153,0.1)]' : 'bg-pe-card border-pe-border'}
    `}>
        <div className="text-xs text-pe-muted uppercase tracking-wider mb-2">{label}</div>
        <div className="font-mono text-2xl font-bold text-pe-accent-text text-center">
            {value}
        </div>
        {subtext && <div className="text-xs text-pe-muted mt-1 text-center">{subtext}</div>}
    </div>
);

// +++ SELECT +++
interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
    label?: string;
    children: React.ReactNode;
}

export const Select: React.FC<SelectProps> = ({ label, children, className = '', ...rest }) => (
    <div className="flex flex-col gap-1 w-full">
         {label && <label className="text-xs font-semibold text-pe-muted uppercase tracking-wider ml-1 mb-1">{label}</label>}
        <div className="relative">
            <select 
                className={`
                    w-full py-2.5 pl-3 pr-8 border border-pe-accent rounded-md bg-pe-input text-pe-accent-text 
                    font-sans font-medium text-base focus:ring-1 focus:ring-pe-accent focus:outline-none 
                    cursor-pointer appearance-none truncate text-center
                    ${className}
                `} 
                {...rest}
            >
                {children}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none text-pe-accent-text">
                <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" /></svg>
            </div>
        </div>
    </div>
);

// +++ COLLAPSIBLE SECTION +++
interface CollapsibleSectionProps {
    title: string;
    initialOpen?: boolean;
    children: React.ReactNode;
    titleClassName?: string; 
    contentClassName?: string; 
}

export const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({ 
    title, 
    initialOpen = false, 
    children,
    titleClassName = '',
    contentClassName = ''
}) => {
    const [isOpen, setIsOpen] = useState(initialOpen);

    return (
        <div className="bg-pe-elevated rounded-lg border border-pe-border mb-6 overflow-hidden transition-all duration-300">
            <button 
                onClick={() => setIsOpen(!isOpen)}
                className={`w-full flex justify-between items-center p-4 bg-pe-elevated dark:bg-slate-800/50 hover:bg-pe-hover dark:hover:bg-slate-800 transition-colors duration-200 focus:outline-none group ${titleClassName}`}
            >
                <h3 className="text-lg font-bold text-pe-text group-hover:text-pe-text font-sans">{title}</h3>
                <svg 
                    className={`w-5 h-5 text-pe-accent-text transition-transform duration-300 ${isOpen ? 'rotate-180' : 'rotate-0'}`} 
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
            </button>
            {isOpen && (
                <div className={`p-6 border-t border-pe-border animation-fade-in ${contentClassName}`}>
                    {children}
                </div>
            )}
        </div>
    );
};

// +++ BUTTON +++
export const Button: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' }> = ({ 
    children, variant = 'primary', className = '', ...rest 
}) => {
    const baseStyle = "px-4 py-2 rounded-md font-bold transition-all duration-200 shadow-md active:scale-95 font-sans";
    const variants = {
        primary: "bg-pe-accent text-black hover:bg-[#d4b088] border border-pe-accent",
        secondary: "bg-pe-card dark:bg-slate-800 text-pe-accent-text border border-pe-accent hover:bg-pe-hover dark:hover:bg-slate-700"
    };
    return (
        <button className={`${baseStyle} ${variants[variant]} ${className}`} {...rest}>
            {children}
        </button>
    );
};

const UI = { Page, Section, PageTitle, SectionTitle, CollapsibleSection, Select, Input, Result, Button };
export default UI;